/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import {
	productDetailsPriceSX,
	productDetailsPriceListSX,
	productDetailsPriceOfferSX,
	productDetailsPriceElectronicsListSX,
} from '@/components/blocks/ProductDetails/styles/priceStrikethru';
import { useDpvalePromotion } from '@/data/Content/Dpvale';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Stack, styled, Tooltip, Typography } from '@mui/material';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { FC, useContext, useMemo } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { InfoElectronico } from '@/components/content/ProductDetails';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { ProductType } from '@/data/types/Product';

const Img = styled('img')({
	height: '24px',
	marginLeft: '4px',
});

export const useProductDetailsPrice = (product?: ProductType, sku?: ProductType) => {
	const { offer, list, currency } = useMemo<{
		offer: number;
		list: number;
		currency: string;
		min: number;
	}>(() => {
		const { prices } = getProductDisplayInfo(sku, product);
		const defaults = { offer: 0, list: 0, currency: '' };
		return { ...defaults, ...prices, min: sku?.productPrice?.min || 0 };
	}, [sku, product]);

	const { price, priceWithoutOffer, additionalOffer } = calculateProductPrices({
		list,
		offer,
		ribbons: sku?.ribbons ?? [],
	});

	return { price, priceWithoutOffer, additionalOffer, offer, list, currency };
};

export const ProductDetailsPrice: FC<{
	infoElectronico?: InfoElectronico;
	orderId?: string;
}> = ({ infoElectronico }) => {
	const localization = useLocalization('PriceDisplay');
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;

	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;

	const { price, priceWithoutOffer, additionalOffer, offer, list, currency } =
		useProductDetailsPrice(product, sku);

	const dpValePromotionData = useMemo(() => {
		if (!product) {
			return undefined;
		}

		return {
			orderId: '',
			product: {
				amount: price.toString(),
				sku: product?.partNumber || '',
				isElectronico: infoElectronico ? 'true' : 'false',
			},
		};
	}, [product, price, infoElectronico]);

	const { data: promotionDpVale } = useDpvalePromotion(dpValePromotionData);

	const { promotions } = promotionDpVale || {};

	const { open, off, toggle } = useIsOpen();
	const isMobile = useIsMobile();

	return infoElectronico ? (
		<Stack direction="row">
			<Stack direction="column" spacing={1} sx={{ marginTop: '9px' }}>
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
					}}
				>
					<Stack>
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', fontStyle: 'italic' }}
						>
							{'Con'}
						</Typography>
					</Stack>
					<Stack>
						<Img src="images/dpvale/dpvale@2x.png" alt="DPVale" />
					</Stack>
					<Stack>
						{isMobile ? (
							<Tooltip
								title="El importe de la compra cambiará al elegir pagar con dpvale en forma de pago, los pagos quincenales pueden presentar cambio por el redondeo de centavos."
								placement="bottom-end"
								open={open}
								onClose={() => off()}
								disableHoverListener
							>
								<InfoIcon sx={{ color: '#969696', cursor: 'pointer' }} onClick={() => toggle()} />
							</Tooltip>
						) : (
							<Tooltip
								title="El importe de la compra cambiará al elegir pagar con dpvale en forma de pago, los pagos quincenales pueden presentar cambio por el redondeo de centavos."
								placement="bottom-end"
								enterTouchDelay={0}
							>
								<InfoIcon sx={{ color: '#969696', cursor: 'pointer' }} />
							</Tooltip>
						)}
					</Stack>
				</Stack>

				{promotions?.normal && promotions?.normal.length ? (
					<Stack>
						<Stack
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
							}}
						>
							<Typography
								sx={{ fontSize: 'var(--font-size-md)', fontWeight: 'bold', color: '#B40309' }}
							>
								<PriceDisplay
									currency={currency}
									min={Math.ceil(Number(promotions?.normal[0].pagoQuincenal))}
									isElectronico={infoElectronico ? true : false}
								/>
							</Typography>
							<Typography
								sx={{
									fontSize: 'var(--font-size-md)',
									fontWeight: 'bold',
									color: '#B40309',
									ml: '5px',
								}}
							>
								{'quincenal'}
							</Typography>
						</Stack>
						<Stack
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
							}}
						>
							<Typography sx={{ fontSize: 'var(--font-size-small)', fontWeight: '600' }}>
								<PriceDisplay
									currency={currency}
									min={Number(promotions?.normal[0].pagoTotal)}
									isElectronico={infoElectronico ? true : false}
								/>
							</Typography>
							<Typography sx={{ fontSize: 'var(--font-size-small)', fontWeight: '600', ml: '5px' }}>
								{'en'}
								{'	'}
								{promotions?.normal[0].quincenas}
								{'	'}
								{'quincenas*'}
							</Typography>
						</Stack>
					</Stack>
				) : null}
			</Stack>
			{/* <Divider orientation="vertical" sx={{ mx: '25px' }} />*/}
			<Stack direction="column" spacing={1} sx={{ marginTop: '9px' }}>
				{/* <Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
					}}
				>
					<Stack>
						<Typography
							sx={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', fontStyle: 'italic' }}
							component={'span'}
						>
							{'De contado'}
						</Typography>
					</Stack>
				</Stack> */}
				{infoElectronico?.prices ? (
					<Stack>
						{!infoElectronico?.prices?.strikedDeContado ? (
							<>
								{/* <Stack>
								<Typography
									component="span"
									data-testid="offer-price"
									id="offer-price"
									sx={productDetailsPriceSX}
								>
									<PriceDisplay
										currency={currency}
										min={Number(infoElectronico?.prices?.offerDeContado)}
										isElectronico={infoElectronico ? true : false}
									/>
								</Typography>
							</Stack>*/}
							</>
						) : (
							<Stack
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}
							>
								<Typography component="span" sx={productDetailsPriceOfferSX}>
									<PriceDisplay
										currency={currency}
										min={Number(infoElectronico?.prices?.offerDeContado)}
										isElectronico={infoElectronico ? true : false}
									/>
								</Typography>
								<Typography component="span" sx={productDetailsPriceElectronicsListSX}>
									<PriceDisplay
										currency={currency}
										min={Number(infoElectronico?.prices?.strikedDeContado)}
										isElectronico={infoElectronico ? true : false}
									/>
								</Typography>
							</Stack>
						)}
					</Stack>
				) : null}
			</Stack>
		</Stack>
	) : (
		<Stack direction="row" spacing={2} sx={{ marginTop: '9px' }}>
			{offer > 0 && offer === list && !additionalOffer ? (
				<Typography
					component="span"
					data-testid="offer-price"
					id="offer-price"
					sx={productDetailsPriceSX}
				>
					<PriceDisplay currency={currency} min={price} />
				</Typography>
			) : (offer > 0 && offer < list) || additionalOffer ? (
				<Typography
					component="span"
					data-testid="offer-price"
					id="offer-price"
					sx={productDetailsPriceOfferSX}
				>
					<PriceDisplay currency={currency} min={price} />
				</Typography>
			) : null}

			{(offer > 0 && offer < list) || additionalOffer ? (
				<Typography
					component="span"
					sx={productDetailsPriceListSX}
					data-testid="list-price"
					id="list-price"
				>
					<PriceDisplay currency={currency} min={priceWithoutOffer} />
				</Typography>
			) : null}

			{offer === 0 ? (
				<Typography variant="h5" component="span" data-testid="no-price" id="no-price">
					{localization.Labels.Pending.t()}
				</Typography>
			) : null}
		</Stack>
	);
};
